import { useEffect, useMemo, useState, type ReactElement } from 'react';

/**
 * Column filters that are built from the rows on the screen.
 *
 * A long queue is unusable without a way to narrow it, and the values worth
 * narrowing by are already sitting in the table — the districts, the vendors,
 * the statuses actually present. Asking a master for them would offer choices
 * that match nothing here, so every option is read from the rows themselves.
 *
 * Two behaviours matter and are easy to get wrong:
 *
 *   - **The options follow the other filters.** Pick one district and every
 *     other list narrows to what is left under it, so a reader can never
 *     assemble a combination that returns nothing.
 *   - **A list never narrows itself.** Its own selection is excluded when its
 *     options are worked out, otherwise choosing one value would wipe the
 *     other values out of the very list you are choosing from and a second
 *     value could never be added.
 *
 * More than one value may be chosen in any list.
 */
export interface FilterableColumn<T> {
  /** Stable key, used for the control's accessible name. */
  readonly key: string;
  /** What the column is called on screen. */
  readonly label: string;
  /**
   * The value this row carries for the column. Null or an empty string means
   * the row has no value, and is offered as its own choice so rows with
   * nothing there can still be found.
   */
  readonly valueOf: (row: T) => string | null | undefined;
}

export interface ColumnFiltersProps<T> {
  readonly columns: ReadonlyArray<FilterableColumn<T>>;
  readonly rows: readonly T[];
  /** Called with the rows that survive every selection. */
  readonly onChange: (visible: readonly T[]) => void;
  /** Accessible name for the whole panel. */
  readonly ariaLabel?: string;
  /** Shown against a value the row does not have. */
  readonly blankLabel?: string;
}

const BLANK = '\u0000blank';

function valueKey(v: string | null | undefined): string {
  const s = (v ?? '').trim();
  return s === '' ? BLANK : s;
}

/** Rows that satisfy every selection except, optionally, one column's own. */
function surviving<T>(
  rows: readonly T[],
  columns: ReadonlyArray<FilterableColumn<T>>,
  chosen: Readonly<Record<string, readonly string[]>>,
  ignoreKey?: string,
): readonly T[] {
  return rows.filter((row) =>
    columns.every((c) => {
      if (c.key === ignoreKey) return true;
      const picked = chosen[c.key];
      if (!picked || picked.length === 0) return true;
      return picked.includes(valueKey(c.valueOf(row)));
    }),
  );
}

export function ColumnFilters<T>({
  columns,
  rows,
  onChange,
  ariaLabel = 'column-filters',
  blankLabel = '(none)',
}: ColumnFiltersProps<T>): ReactElement {
  const [chosen, setChosen] = useState<Record<string, readonly string[]>>({});

  const visible = useMemo(
    () => surviving(rows, columns, chosen),
    [rows, columns, chosen],
  );

  // Reported after the render that produced it, never during: calling back
  // into the parent while rendering is what makes React complain about
  // updating one component from inside another.
  //
  // `onChange` must be stable (useCallback) in the parent, or this runs on
  // every render — the same contract every callback prop here carries.
  useEffect(() => {
    onChange(visible);
  }, [visible, onChange]);

  const optionsFor = (c: FilterableColumn<T>): Array<{ v: string; n: number }> => {
    const pool = surviving(rows, columns, chosen, c.key);
    const counts = new Map<string, number>();
    for (const row of pool) {
      const k = valueKey(c.valueOf(row));
      counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    // A value already chosen stays listed even if nothing matches it any more,
    // so it can always be unchosen.
    for (const k of chosen[c.key] ?? []) {
      if (!counts.has(k)) counts.set(k, 0);
    }
    return [...counts.entries()]
      .map(([v, n]) => ({ v, n }))
      .sort((a, b) => (a.v === BLANK ? 1 : b.v === BLANK ? -1 : a.v.localeCompare(b.v)));
  };

  const activeCount = Object.values(chosen).reduce((n, v) => n + v.length, 0);

  return (
    <section aria-label={ariaLabel}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
        {columns.map((c) => {
          const opts = optionsFor(c);
          const picked = chosen[c.key] ?? [];
          return (
            <label
              key={c.key}
              style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}
            >
              <span>
                {c.label}
                {picked.length > 0 ? ` (${picked.length})` : ''}
              </span>
              <select
                multiple
                aria-label={`filter-${c.key}`}
                data-testid={`filter-${c.key}`}
                value={[...picked]}
                size={Math.min(5, Math.max(2, opts.length))}
                onChange={(e) => {
                  const next = [...e.target.selectedOptions].map((o) => o.value);
                  setChosen((prev) => ({ ...prev, [c.key]: next }));
                }}
                style={{ minWidth: 140 }}
              >
                {opts.map((o) => (
                  <option key={o.v} value={o.v}>
                    {(o.v === BLANK ? blankLabel : o.v) + ` (${o.n})`}
                  </option>
                ))}
              </select>
            </label>
          );
        })}
      </div>
      <p style={{ fontSize: 12, margin: '6px 0 0' }}>
        <span data-testid="filter-count">
          Showing {visible.length} of {rows.length}
        </span>
        {activeCount > 0 ? (
          <>
            {' '}
            <button
              type="button"
              data-testid="filter-clear"
              aria-label="clear-filters"
              onClick={() => setChosen({})}
            >
              Clear filters
            </button>
          </>
        ) : null}
      </p>
    </section>
  );
}
